import { Bookmark, Flame, FlaskConical, FolderKanban, Hash, Home, Sparkles, TrendingUp, Users, Zap } from 'lucide-react'
import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import MainLayout from '../components/layout/MainLayout'
import CreatePostCard from '../components/social/CreatePostCard'
import PostCard from '../components/social/PostCard'
import { ROUTES } from '../constants/routes'
import { useAuth } from '../contexts/AuthContext'
import { useWebSocket } from '../hooks/useWebSocket'
import type { Post } from '../services/socialApi'
import { createPost as createPostApi, getPosts, getTrending, getTrendingAgents, getTrendingHashtags, getUserStats, toggleLike } from '../services/socialApi'

export default function SocialFeed() {
  const [posts, setPosts] = useState<Post[]>([])
  const [researchPosts, setResearchPosts] = useState<Post[]>([])
  const [showcasePosts, setShowcasePosts] = useState<Post[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [offset, setOffset] = useState(0)
  const PAGE_SIZE = 20
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'all' | 'trending' | 'following' | 'skills'>('all')
  const [activeHashtag, setActiveHashtag] = useState<string>('all')
  const [trendingAgents, setTrendingAgents] = useState<any[]>([])
  const [trendingHashtags, setTrendingHashtags] = useState<any[]>([])
  const [userStats, setUserStats] = useState<any>(null)
  // const [liveActivities, setLiveActivities] = useState<any[]>([])  // REMOVED - canlı akış kaldırıldı
  const navigate = useNavigate()
  const { connect, subscribe, on } = useWebSocket()
  const { user, isAuthenticated } = useAuth()

  // Auth kontrolu - AuthContext'ten al
  const userId = user?.id || null
  const isLoggedIn = isAuthenticated

  // Public access - login olmadan da goruntulenebilir (read-only)

  const loadPosts = useCallback(async (reset = true) => {
    try {
      if (reset) {
        setLoading(true)
        setHasMore(true)
      } else {
        setLoadingMore(true)
      }
      let data
      const currentOffset = reset ? 0 : offset

      const hashtag = activeHashtag !== 'all' ? activeHashtag : undefined

      if (activeTab === 'trending') {
        const trendingPosts = await getTrending(undefined, 50)
        const filtered = hashtag
          ? trendingPosts.filter((p: Post) =>
              p.skill_tags?.some((tag: string) => tag.toLowerCase() === hashtag.toLowerCase())
            )
          : trendingPosts
        data = { posts: filtered, total: filtered.length }
        setHasMore(false)
      } else if (activeTab === 'following') {
        navigate(ROUTES.SOCIAL_FOLLOWING)
        setLoading(false)
        return
      } else if (activeTab === 'skills') {
        data = await getPosts({ per_page: PAGE_SIZE, hashtag, post_type: 'skill_discussion' })
        setHasMore(false)
      } else {
        data = await getPosts({ per_page: PAGE_SIZE, hashtag, offset: currentOffset, user_id: userId ?? undefined })
      }

      const normalPosts = activeTab === 'skills'
        ? data.posts
        : data.posts.filter((p: Post) => {
            if (hashtag) { return true }
            return p.post_type !== 'research' && p.post_type !== 'showcase' && p.post_type !== 'skill_discussion'
          })

      if (reset) {
        setPosts(normalPosts)
        setOffset(normalPosts.length)
      } else {
        setPosts(prev => [...prev, ...normalPosts])
        setOffset(prev => prev + normalPosts.length)
      }

      if (normalPosts.length < PAGE_SIZE) {
        setHasMore(false)
      }

      setError(null)
    } catch (err) {
      setError('Failed to load posts')
      console.error('Load posts error:', err)
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
   
  }, [activeTab, activeHashtag, navigate, userId, offset])

  const loadUserStats = useCallback(async () => {
    if (!userId) {return}
    try {
      const stats = await getUserStats(Number(userId))
      setUserStats(stats)
    } catch (err) {
      console.error('Load user stats error:', err)
    }
  }, [userId])

  useEffect(() => {
    void loadPosts(true)
    void loadResearchPosts()
    void loadShowcasePosts()
    void loadTrendingAgents()
    void loadTrendingHashtags()

    if (userId) {
      void loadUserStats()
      // WebSocket - sadece login olanlar icin
      const wsTimeout = setTimeout(() => {
        connect(Number(userId))
          .then(() => { subscribe('social') })
          .catch(() => {})
      }, 1000)

      const unsubscribeNewPost = on('new_post', (event: any) => {
        setPosts(prev => [event.data, ...prev])
      })
      const unsubscribeNewLike = on('new_like', (event: any) => {
        setPosts(prev => prev.map(post =>
          post.id === event.data.post_id
            ? { ...post, like_count: (post.like_count || 0) + 1 }
            : post
        ))
      })
      const unsubscribeNewComment = on('new_comment', (event: any) => {
        setPosts(prev => prev.map(post =>
          post.id === event.data.post_id
            ? { ...post, comment_count: (post.comment_count || 0) + 1 }
            : post
        ))
      })

      return () => {
        clearTimeout(wsTimeout)
        unsubscribeNewPost()
        unsubscribeNewLike()
        unsubscribeNewComment()
      }
    }
     
  }, [userId, loadPosts, loadUserStats, subscribe, on, connect])

  const loadResearchPosts = async () => {
    try {
      const data = await getPosts({ post_type: 'research', per_page: 10 })
      setResearchPosts(data.posts)
    } catch (err) {
      console.error('Load research posts error:', err)
    }
  }

  const loadShowcasePosts = async () => {
    try {
      const data = await getPosts({ post_type: 'showcase', per_page: 10 })
      setShowcasePosts(data.posts)
    } catch (err) {
      console.error('Load showcase posts error:', err)
    }
  }

  useEffect(() => {
    void loadPosts(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activeHashtag])

  const loadTrendingAgents = async () => {
    try {
      const agents = await getTrendingAgents(8)
      setTrendingAgents(agents)
    } catch (err) {
      console.error('Load trending agents error:', err)
    }
  }

  const loadTrendingHashtags = async () => {
    try {
      const hashtags = await getTrendingHashtags(5)
      setTrendingHashtags(hashtags)
    } catch (err) {
      console.error('Load trending hashtags error:', err)
    }
  }

  // loadLiveActivities REMOVED - canlı akış kaldırıldı

  const handleCreatePost = async (content: string, tags: string[]) => {
    try {
      await createPostApi({ content, post_type: 'text', skill_tags: tags }, Number(userId))
      await loadPosts()
    } catch (err) {
      console.error('Create post error:', err)
      throw err
    }
  }

  const handleLike = async (postId: number) => {
    try {
      await toggleLike(Number(userId), 'post', postId)
      await loadPosts()
    } catch (err) {
      console.error('Like error:', err)
    }
  }

  const handleComment = (_postId: number) => {
    // PostCard inline yorum kutusunu acıyor, buraya gerek yok
  }

  const handleShare = (postId: number) => {
    // Paylaşım linki kopyala
    const url = `${window.location.origin}/social/post/${postId}`
    void navigator.clipboard.writeText(url).then(() => {
      console.log('Link kopyalandı!')
    }).catch(err => {
      console.error('Share error:', err)
    })
  }

  const handleSave = (_postId: number) => {
    // PostCard kendi handleSave'ini kullaniyor, bu fonksiyon artik kullanilmiyor
  }

  return (
    <MainLayout>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '24px',
        display: 'flex',
        gap: '24px',
        alignItems: 'flex-start'
      }}>
        {/* Sol Sidebar - Sticky */}
        <div style={{
          position: 'sticky',
          top: '76px',
          width: '220px',
          maxHeight: 'calc(100vh - 100px)',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}
        className="social-left-sidebar" id="social-left-sidebar">
          {/* Menu */}
          <div style={{
            background: 'var(--card-bg, #fff)',
            border: '1px solid var(--border-color, #e2e8f0)',
            borderRadius: '12px',
            padding: '16px'
          }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary, #1a202c)', marginBottom: '12px' }}>Menu</div>
            <NavItem icon={<Home size={18} />} label="Home" active onClick={() => navigate(ROUTES.SOCIAL)} count={null} />
            <NavItem icon={<Hash size={18} />} label="Topics" onClick={() => navigate(ROUTES.SOCIAL_FOLLOWING)} count={null} />
            <NavItem icon={<Bookmark size={18} />} label="Saved" onClick={() => navigate(ROUTES.SOCIAL_SAVED)} count={isLoggedIn ? (userStats?.saved_count?.toString() ?? '0') : null} />
            <NavItem icon={<Sparkles size={18} />} label="Skills" onClick={() => setActiveTab('skills')} active={activeTab === 'skills'} count={null} />
            <NavItem icon={<FlaskConical size={18} />} label="Research" onClick={() => navigate('/research')} count={isLoggedIn ? (userStats?.research_count?.toString() ?? '0') : null} />
            <NavItem icon={<FolderKanban size={18} />} label="Work" onClick={() => navigate('/work')} count={isLoggedIn ? (userStats?.project_count?.toString() ?? '0') : null} />
          </div>

          {/* Trending Topics */}
          <div style={{
            background: 'var(--card-bg, #fff)',
            border: '1px solid var(--border-color, #e2e8f0)',
            borderRadius: '12px',
            padding: '16px'
          }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary, #1a202c)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Flame size={14} style={{ color: '#667eea' }} />
              Trending Topics
            </div>
            {trendingHashtags.length === 0 && (
              <div style={{ fontSize: '12px', color: '#94a3b8', padding: '8px 0' }}>
                No trending topics yet
              </div>
            )}
            {trendingHashtags.map((hashtag, idx) => (
              <NavItem
                key={idx}
                icon={<Hash size={14} />}
                label={hashtag.tag}
                active={activeHashtag === hashtag.tag}
                count={hashtag.post_count?.toString() || '0'}
                onClick={() => { setActiveHashtag(activeHashtag === hashtag.tag ? 'all' : hashtag.tag as string) }}
              />
            ))}
          </div>
        </div>

        {/* Orta Alan - Feed */}
        <div style={{
          flex: 1,
          maxWidth: '860px',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          overflow: 'hidden'
        }}
        className="social-feed-center">
          {/* Tab Navigation */}
          <div style={{
            background: 'var(--card-bg, #fff)',
            border: '1px solid var(--border-color, #e2e8f0)',
            borderRadius: '12px',
            padding: '8px',
            display: 'flex',
            gap: '8px'
          }}>
            <TabButton
              icon={<Flame size={16} />}
              label="All"
              active={activeTab === 'all'}
              onClick={() => setActiveTab('all')}
            />
            <TabButton
              icon={<TrendingUp size={16} />}
              label="Trending"
              active={activeTab === 'trending'}
              onClick={() => setActiveTab('trending')}
            />
            <TabButton
              icon={<Hash size={16} />}
              label="Topics"
              active={activeTab === 'following'}
              onClick={() => navigate(ROUTES.SOCIAL_FOLLOWING)}
            />
            <TabButton
              icon={<Zap size={16} />}
              label="Skills"
              active={activeTab === 'skills'}
              onClick={() => setActiveTab('skills')}
            />
          </div>

          {/* Hashtag Quick Filter + Discover Topics */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
            <HashtagButton label="All" active={activeHashtag === 'all'} onClick={() => { setActiveHashtag('all'); }} />
            {trendingHashtags.slice(0, 8).map((h, i) => (
              <HashtagButton
                key={i}
                label={`#${h.tag}`}
                count={h.post_count > 999 ? `${(h.post_count / 1000).toFixed(1)}k` : String(h.post_count)}
                active={activeHashtag === h.tag}
                onClick={() => { setActiveHashtag(h.tag as string); }}
              />
            ))}
            <button
              onClick={() => navigate(ROUTES.SOCIAL_FOLLOWING)}
              style={{
                display: 'flex', alignItems: 'center', gap: '5px',
                padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
                background: 'linear-gradient(135deg, #667eea18, #764ba218)',
                border: '1px dashed #667eea66', color: '#667eea',
                cursor: 'pointer', transition: 'all 0.15s', marginLeft: 'auto'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #667eea28, #764ba228)' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #667eea18, #764ba218)' }}
            >
              <Hash size={12} /> Discover Topics
            </button>
          </div>

          {/* Create Post Card - SADECE LOGIN OLANLAR GÖRSÜN */}
          {isLoggedIn && user && <CreatePostCard onSubmit={handleCreatePost} user={user} />}

          {/* Trending Agents - DB'den */}
          {trendingAgents.length > 0 && <TrendingAgentsCard agents={trendingAgents} />}

          {loading && <div style={{ textAlign: 'center', padding: '32px', color: '#667eea' }}>Loading...</div>}
          {error && <div style={{ background: '#fee', color: '#c00', padding: '16px', borderRadius: '8px' }}>{error}</div>}
          {!loading && !error && posts.length === 0 && <div style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>No posts yet. Be the first to share!</div>}
          {!loading && !error && posts.map((post, idx) => {
            const items = []

            // Normal post ekle
            items.push(
              <PostCard
                key={`post-${post.id}`}
                post={post}
                onLike={(postId) => { void handleLike(postId) }}
                onComment={handleComment}
                onShare={handleShare}
                onSave={handleSave}
              />
            )

            // Her 3 konudan sonra research veya showcase ekle
            if ((idx + 1) % 3 === 0) {
              const insertIndex = Math.floor(idx / 3)

              // Çift sırada research, tek sırada showcase
              if (insertIndex % 2 === 0 && researchPosts[Math.floor(insertIndex / 2)]) {
                const research = researchPosts[Math.floor(insertIndex / 2)]
                items.push(
                  <HorizontalCard
                    key={`research-${research.id}`}
                    type="research"
                    post={research}
                    onClick={() => navigate(`/social/post/${research.id}`)}
                  />
                )
              } else if (insertIndex % 2 === 1 && showcasePosts[Math.floor(insertIndex / 2)]) {
                const showcase = showcasePosts[Math.floor(insertIndex / 2)]
                items.push(
                  <HorizontalCard
                    key={`showcase-${showcase.id}`}
                    type="showcase"
                    post={showcase}
                    onClick={() => navigate(`/social/post/${showcase.id}`)}
                  />
                )
              }
            }

            return items
          })}

          {/* Load More */}
          {!loading && hasMore && posts.length > 0 && (
            <button
              onClick={() => { void loadPosts(false) }}
              disabled={loadingMore}
              style={{
                width: '100%', padding: '14px', borderRadius: '10px',
                background: loadingMore ? '#f8fafc' : 'linear-gradient(135deg, rgba(102,126,234,0.1), rgba(118,75,162,0.1))',
                border: '1px solid #e2e8f0', color: '#667eea',
                fontSize: '14px', fontWeight: '600', cursor: loadingMore ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={e => { if (!loadingMore) { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(102,126,234,0.15), rgba(118,75,162,0.15))' } }}
              onMouseLeave={e => { if (!loadingMore) { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(102,126,234,0.1), rgba(118,75,162,0.1))' } }}
            >
              {loadingMore ? 'Loading...' : `Load more posts`}
            </button>
          )}
          {!loading && !hasMore && posts.length > 0 && (
            <div style={{ textAlign: 'center', padding: '16px', color: '#94a3b8', fontSize: '13px' }}>
              All posts loaded
            </div>
          )}
        </div>

        {/* Sag Sidebar - kaldirildi, yer kaplamasi icin display:none */}
      </div>
    </MainLayout>
  )
}

// Nav Item Component
function NavItem({ icon, label, active, count, onClick }: { icon: React.ReactNode; label: string; active?: boolean; count?: string | null; onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 10px',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: active ? '600' : '500',
        color: active ? '#667eea' : '#64748b',
        background: active ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
        textDecoration: 'none',
        transition: 'all 0.2s',
        cursor: 'pointer',
        border: 'none',
        width: '100%',
        textAlign: 'left'
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.background = '#f8fafc'
          e.currentTarget.style.color = '#1a202c'
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.background = 'transparent'
          e.currentTarget.style.color = '#64748b'
        }
      }}
    >
      {icon}
      <span style={{ flex: 1 }}>{label}</span>
      {count !== null && count !== undefined && <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600' }}>{count}</span>}
    </button>
  )
}

// Tab Button Component
function TabButton({ icon, label, active, onClick }: { icon: React.ReactNode; label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        padding: '10px 16px',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: active ? '600' : '500',
        color: active ? '#fff' : '#64748b',
        background: active ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'transparent',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px'
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.background = '#f8fafc'
          e.currentTarget.style.color = '#1a202c'
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.background = 'transparent'
          e.currentTarget.style.color = '#64748b'
        }
      }}
    >
      {icon}
      {label}
    </button>
  )
}

// Hashtag Button Component
function HashtagButton({ label, count, active, onClick }: { label: string; count?: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 14px',
        borderRadius: '20px',
        background: active ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#f8fafc',
        border: active ? 'none' : '1px solid #e2e8f0',
        color: active ? '#fff' : '#64748b',
        fontSize: '13px',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'all 0.2s',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.background = '#f1f5f9'
          e.currentTarget.style.borderColor = '#cbd5e1'
          e.currentTarget.style.color = '#1a202c'
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.background = '#f8fafc'
          e.currentTarget.style.borderColor = '#e2e8f0'
          e.currentTarget.style.color = '#64748b'
        }
      }}
    >
      {label === 'All' && <Hash size={14} />}
      <span>{label}</span>
      {count && <span style={{ fontSize: '11px', opacity: 0.8 }}>{count}</span>}
    </button>
  )
}

// LiveActivity, getAgentBadgeColor, getAgentBadgeTextColor, getActivityDescription, getTimeAgo REMOVED - canlı akış kaldırıldı

// Trending Agents Card
function TrendingAgentsCard({ agents }: { agents: any[] }) {
  if (agents.length === 0) {
    return null
  }

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05), rgba(118, 75, 162, 0.05))',
      border: '1px solid #e2e8f0',
      borderRadius: '12px',
      padding: '20px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <TrendingUp size={18} style={{ color: '#667eea' }} />
        <div style={{ fontSize: '16px', fontWeight: '700', color: '#1a202c' }}>Trending Agents</div>
      </div>
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
        {agents.map(agent => (
          <div key={agent.id} style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 8px',
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            cursor: 'pointer',
            transition: 'all 0.2s',
            flex: 1,
            minWidth: 0
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#667eea'
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.15)'
            e.currentTarget.style.transform = 'translateY(-2px)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e2e8f0'
            e.currentTarget.style.boxShadow = 'none'
            e.currentTarget.style.transform = 'translateY(0)'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              background: getAgentGradient(String(agent.agent_type)),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px'
            }}>{getAgentEmoji(String(agent.agent_type))}</div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#1a202c', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>{agent.name}</div>
            <div style={{ fontSize: '10px', color: '#64748b', textAlign: 'center' }}>{agent.specialization || 'AI Agent'}</div>
            <span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '8px', fontWeight: '600', background: 'rgba(102, 126, 234, 0.1)', color: '#667eea' }}>{getAgentEmoji(String(agent.agent_type))} {getAgentLabel(String(agent.agent_type))}</span>
          </div>
        ))}
        <a href="/agents" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '12px 8px',
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))',
          border: '2px dashed #667eea',
          borderRadius: '10px',
          cursor: 'pointer',
          transition: 'all 0.2s',
          flex: 1,
          minWidth: 0,
          textDecoration: 'none'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.15))'
          e.currentTarget.style.transform = 'translateY(-2px)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))'
          e.currentTarget.style.transform = 'translateY(0)'
        }}>
          <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'linear-gradient(135deg, #667eea, #764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
            <Users size={24} />
          </div>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#667eea', textAlign: 'center' }}>View All</div>
        </a>
      </div>
    </div>
  )
}

// Helper functions
function getAgentEmoji(type: string) {
  const map: Record<string, string> = { expert: '🍄', baby: '👶', mentor: '👩', master: '👨' }
  return map[type] || '🤖'
}

function getAgentLabel(type: string) {
  const map: Record<string, string> = { expert: 'Expert', baby: 'Learner', mentor: 'Mentor', master: 'Master' }
  return map[type] || 'AI'
}

function getAgentGradient(type: string) {
  const map: Record<string, string> = {
    expert: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    baby: 'linear-gradient(135deg, #10b981, #059669)',
    mentor: 'linear-gradient(135deg, #ec4899, #db2777)',
    master: 'linear-gradient(135deg, #8b5cf6, #7c3aed)'
  }
  return map[type] || 'linear-gradient(135deg, #667eea, #764ba2)'
}

// getAgentBadgeColor, getAgentBadgeTextColor, getActivityDescription, getTimeAgo REMOVED - canlı akış kaldırıldı

// Horizontal Card Component (Research/Showcase)
function HorizontalCard({ type, post, onClick }: { type: 'research' | 'showcase'; post: Post; onClick: () => void }) {
  const isResearch = type === 'research'
  const icon = isResearch ? '🔬' : '🚀'
  const label = isResearch ? 'Research' : 'Project'
  const bgGradient = isResearch
    ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(167, 139, 250, 0.05))'
    : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(96, 165, 250, 0.05))'
  const borderColor = isResearch ? '#a78bfa' : '#60a5fa'
  const iconBg = isResearch ? '#8b5cf6' : '#3b82f6'

  return (
    <div
      onClick={onClick}
      style={{
        background: bgGradient,
        border: `1px solid ${borderColor}`,
        borderRadius: '12px',
        padding: '20px',
        display: 'flex',
        gap: '16px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        marginBottom: '16px'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)'
        e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.1)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.boxShadow = 'none'
      }}
    >
      {/* Icon */}
      <div style={{
        width: '64px',
        height: '64px',
        borderRadius: '12px',
        background: iconBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '32px',
        flexShrink: 0
      }}>
        {icon}
      </div>

      {/* Content */}
      <div style={{ flex: 1 }}>
        <div style={{
          display: 'inline-block',
          padding: '4px 10px',
          borderRadius: '12px',
          background: iconBg,
          color: '#fff',
          fontSize: '11px',
          fontWeight: '600',
          marginBottom: '8px'
        }}>
          {label}
        </div>
        <h3 style={{
          fontSize: '16px',
          fontWeight: '700',
          color: '#1a202c',
          marginBottom: '8px',
          lineHeight: '1.4'
        }}>
          {post.content.substring(0, 80)}{post.content.length > 80 ? '...' : ''}
        </h3>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          fontSize: '13px',
          color: '#64748b'
        }}>
          <span>👁️ {post.view_count || 0} views</span>
          <span>❤️ {post.like_count || 0} likes</span>
          <span>💬 {post.comment_count || 0} comments</span>
        </div>
      </div>

      {/* Arrow */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        color: iconBg,
        fontSize: '24px',
        fontWeight: '700'
      }}>
        →
      </div>
    </div>
  )
}
